import React, { useState, useCallback } from 'react';
import { Mail, RefreshCw, Send, FileText, MailX, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MailMergeManager from '@/components/leads/mailmerge/MailMergeManager';
import CampaignManager from '@/components/campaigns/CampaignManager';
import CampaignTemplatesList from '@/components/campaigns/CampaignTemplatesList';
import CampaignUnsubscribeManager from '@/components/campaigns/CampaignUnsubscribeManager';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const MailMerges = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('campaigns');
  const queryClient = useQueryClient();

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleCampaignCreated = useCallback(() => {
    setActiveTab('campaigns');
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, [queryClient]);

  // Get user's company_id
  const { data: profile } = useQuery({
    queryKey: ['current-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase.from('profiles').select('company_id').eq('id', user.id).single();
      return data;
    },
  });

  const companyId = profile?.company_id;

  return (
    <main className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 w-full">
      {/* Page Header */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-r from-primary/5 via-accent/5 to-background border border-primary/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
                Outreach
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Email campaigns, templates, and mail merge history
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} className="self-start sm:self-auto">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} key={refreshKey}>
        <TabsList className="w-full sm:w-auto mb-4">
          <TabsTrigger value="campaigns" className="gap-1.5">
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Campaigns</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="unsubscribes" className="gap-1.5">
            <MailX className="h-4 w-4" />
            <span className="hidden sm:inline">Unsubscribes</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Mail Merge</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          {companyId ? <CampaignManager companyId={companyId} /> : <p className="text-center text-muted-foreground py-8">No company assigned.</p>}
        </TabsContent>

        <TabsContent value="templates">
          {companyId ? <CampaignTemplatesList companyId={companyId} /> : <p className="text-center text-muted-foreground py-8">No company assigned.</p>}
        </TabsContent>

        <TabsContent value="unsubscribes">
          <CampaignUnsubscribeManager companyId={companyId || ''} />
        </TabsContent>

        <TabsContent value="history">
          <MailMergeManager open={true} onOpenChange={() => {}} asPage onCampaignCreated={handleCampaignCreated} />
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default MailMerges;
